import React from "react";
import { FileCsv as FileCsvIcon } from "@phosphor-icons/react";
import { File, Mail } from "lucide-react";

import OnenoteIcon from "../../assets/Icons/onenote.svg";
import PdfIcon from "../../assets/Icons/pdf.svg";
import DocxIcon from "../../assets/Icons/docx.svg";
import ExcelIcon from "../../assets/Icons/excel.svg";
import PptxIcon from "../../assets/Icons/pptx.svg";
import EvestmentIcon from "../../assets/Icons/evestment.svg";
import AltivaIcon from "../../assets/Icons/altiva.svg";
import ImageFileIcon from "../../assets/Icons/image.svg";
import VideoFileIcon from "../../assets/Icons/video.svg";
import AudioFileIcon from "../../assets/Icons/audio.svg";
import TextFileIcon from "../../assets/Icons/text.svg";
import { ViewerFileType } from "./services/fileViewerInterface";
import { kindForExt } from "../FpPages/Artifacts/artifactBadge";

const ICON_CLASS = "w-[14px] min-w-[14px] mr-2 ml-1";

/** The document extensions the header has an icon for, each mapped to the kind
 *  whose icon it is. The name wins over the kind the viewer is rendering,
 *  because a file previewed in another form is still the file the user has: an
 *  Office document a chat turn produced reaches the viewer as a server-side PDF
 *  render (#9840), and used to be labelled as one (#11609). Mapping to a kind
 *  rather than straight to an icon keeps one icon per kind. */
const KIND_BY_EXTENSION: Record<string, ViewerFileType> = {
  pdf: "pdf",
  doc: "docx",
  docx: "docx",
  xls: "excel",
  xlsx: "excel",
  xlsm: "excel",
  csv: "csv",
  ppt: "pptx",
  pptx: "pptx",
};

/** The kind a file's own name claims, for the names that claim one. Everything
 *  else — interaction notes, emails, OneNote pages, connector records — keeps
 *  the icon its kind carries. */
const kindFromFileName = (fileName: string): ViewerFileType | undefined =>
  KIND_BY_EXTENSION[fileName.split(".").at(-1)?.toLowerCase() ?? ""];

/** The type tile for the document viewer's header (#12481): the same tile
 *  the artifact panes wear, so the two headers read as one. A file's own
 *  extension names it; a kind with no extension (a note, an email, a
 *  connector record) gets a short word for its kind. */
export const fileTypeBadge = (
  fileName: string,
  fileType: ViewerFileType | null | undefined,
  contentType?: string
): { ext: string; kind: string } => {
  const ext = fileName.split(".").at(-1)?.toLowerCase() ?? "";
  const kind = kindFromFileName(fileName) ?? fileType;
  if (kind && KIND_BY_EXTENSION[ext] === kind) {
    return { ext: ext.toUpperCase(), kind: kindForExt(ext) };
  }
  switch (kind) {
    case "pdf":
      return { ext: "PDF", kind: "PDF" };
    case "docx":
      return { ext: "DOCX", kind: "Document" };
    case "artifact":
    case "excel":
      return { ext: "XLSX", kind: "Spreadsheet" };
    case "csv":
      return { ext: "CSV", kind: "CSV" };
    case "pptx":
      return { ext: "PPTX", kind: "Presentation" };
    case "onenote-page":
      return { ext: "NOTE", kind: "OneNote page" };
    case "evestment-note":
      return { ext: "NOTE", kind: "eVestment note" };
    case "email":
      return { ext: "EMAIL", kind: "Email" };
    case "altvia_interaction":
    case "altvia_interaction_note":
      return { ext: "ALT", kind: "Altvia record" };
    case "finpilot-interaction":
      return { ext: "NOTE", kind: "Note" };
    case "unsupported": {
      const category = (contentType?.split(";")[0]?.trim() ?? "").split("/")[0];
      if (category === "image") return { ext: "IMG", kind: "Image" };
      if (category === "video") return { ext: "VID", kind: "Video" };
      if (category === "audio") return { ext: "AUD", kind: "Audio" };
      if (category === "text") return { ext: "TXT", kind: "Text file" };
      return {
        ext: ext ? ext.toUpperCase().slice(0, 4) : "FILE",
        kind: "File",
      };
    }
    default:
      return {
        ext: ext ? ext.toUpperCase().slice(0, 4) : "FILE",
        kind: "File",
      };
  }
};

export interface FileTypeHeaderIconProps {
  /** The file's own name, suffix included — not the name the header prints,
   *  which for an interaction is a title with its `.fp` stripped off. */
  fileName: string;
  /** The kind the viewer is rendering the file as. */
  fileType: ViewerFileType | null | undefined;
  /** The preview response's `Content-Type`, which is all there is to go on for
   *  a file no viewer can render. */
  contentType?: string;
}

/** The file-type icon beside the name in the document viewer header. */
const FileTypeHeaderIcon = ({
  fileName,
  fileType,
  contentType,
}: FileTypeHeaderIconProps): React.ReactElement | null => {
  const kind = kindFromFileName(fileName) ?? fileType;
  if (!kind) return null;

  switch (kind) {
    case "csv":
      return <FileCsvIcon role="img" aria-label="CSV" className={ICON_CLASS} />;
    case "pdf":
      return <img src={PdfIcon} alt="PDF" className={ICON_CLASS} />;
    case "onenote-page":
      return (
        <img src={OnenoteIcon} alt="OneNote page" className={ICON_CLASS} />
      );
    case "artifact":
    case "excel":
      return (
        <img src={ExcelIcon} alt="Excel spreadsheet" className={ICON_CLASS} />
      );
    case "docx":
      return <img src={DocxIcon} alt="Word document" className={ICON_CLASS} />;
    case "pptx":
      return (
        <img
          src={PptxIcon}
          alt="PowerPoint presentation"
          className={ICON_CLASS}
        />
      );
    case "evestment-note":
      return (
        <img
          src={EvestmentIcon}
          alt="eVestment note"
          className="w-[18px] min-w-[18px] mr-2 ml-1"
        />
      );
    case "email":
      return <Mail role="img" aria-label="Email" className={ICON_CLASS} />;
    case "altvia_interaction":
    case "altvia_interaction_note":
      return (
        <img src={AltivaIcon} alt="Altvia record" className={ICON_CLASS} />
      );
    case "finpilot-interaction":
      return null;
    case "unsupported": {
      const category = (contentType?.split(";")[0]?.trim() ?? "").split("/")[0];
      if (category === "image")
        return <img src={ImageFileIcon} alt="Image" className={ICON_CLASS} />;
      if (category === "video")
        return <img src={VideoFileIcon} alt="Video" className={ICON_CLASS} />;
      if (category === "audio")
        return <img src={AudioFileIcon} alt="Audio" className={ICON_CLASS} />;
      if (category === "text")
        return (
          <img src={TextFileIcon} alt="Text file" className={ICON_CLASS} />
        );
      return <File role="img" aria-label="File" className={ICON_CLASS} />;
    }
    default:
      return <File role="img" aria-label="File" className={ICON_CLASS} />;
  }
};

export default FileTypeHeaderIcon;
